/**
 * Coming up as an agenda: dated items bucketed Overdue / This week / This
 * month / Later, each with relative wording ("in 3 days", "2 weeks overdue")
 * and the prompt Home's composer gets when you ask Amoreg to prepare it.
 *
 * @module components/home/HomeAgenda.logic
 */
import type { HomeComingUpItem } from "./homeLogic";

export type AgendaBucket = "overdue" | "week" | "month" | "later";

export const AGENDA_BUCKET_LABEL: Readonly<Record<AgendaBucket, string>> = {
  overdue: "Overdue",
  week: "This week",
  month: "This month",
  later: "Later",
};

const DAY = 86_400_000;

/** Whole days from today to the item's day, in local time (negative = past). */
export function daysUntil(at: number, now: number): number {
  const start = (time: number) => {
    const date = new Date(time);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  };
  return Math.round((start(at) - start(now)) / DAY);
}

export function agendaBucket(at: number, now: number): AgendaBucket {
  const days = daysUntil(at, now);
  if (days < 0) return "overdue";
  if (days <= 7) return "week";
  if (days <= 31) return "month";
  return "later";
}

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? "" : "s"}`;

/** "today", "tomorrow", "in 3 days", "in 2 weeks", "in 4 months"; past: "1 day overdue", "5 days overdue". */
export function relativeDue(at: number, now: number): string {
  const days = daysUntil(at, now);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "1 day overdue";
  const size = Math.abs(days);
  const span =
    size < 14 ? plural(size, "day") : size < 60 ? plural(Math.round(size / 7), "week") : plural(Math.round(size / 30), "month");
  return days > 0 ? `in ${span}` : `${span} overdue`;
}

export interface AgendaSection<T> {
  readonly bucket: AgendaBucket;
  readonly items: ReadonlyArray<T>;
}

/** Buckets in agenda order, empty ones dropped; items keep their order. */
export function agendaSections<T extends Pick<HomeComingUpItem, "at">>(
  items: ReadonlyArray<T>,
  now: number,
): AgendaSection<T>[] {
  const order: AgendaBucket[] = ["overdue", "week", "month", "later"];
  return order
    .map((bucket) => ({ bucket, items: items.filter((item) => agendaBucket(Date.parse(item.at), now) === bucket) }))
    .filter((section) => section.items.length > 0);
}

const dateWords = (at: number) =>
  new Date(at).toLocaleDateString("en-GB", { day: "numeric", month: "long" });

/** What "Ask Amoreg to prepare" puts in the composer for this item. */
export function preparePrompt(item: Pick<HomeComingUpItem, "kind" | "title" | "at" | "context">): string {
  const when = dateWords(Date.parse(item.at));
  switch (item.kind) {
    case "review":
      return `Prepare the ${item.context ?? item.title} review due ${when}: what has changed since the last review, and what should we update?`;
    case "effective":
      return `What does “${item.title}” change for us before it takes effect on ${when}?`;
    case "comments":
      return `Help us draft our comments on “${item.title}” before the deadline on ${when}.`;
  }
}
